import { Services } from "./Services";
import { HttpAPIService } from "@/core/api/HttpAPIService";
import { SentryLoggerService } from "@/core/Logger/SentryLoggerService";
import { createQueryBuilderFactory } from "@/core/api/QueryBuilderFactory";
import { StoreGroupService } from "@/modules/storeGroups/api/StoreGroupService";
import appConfiguration from "@/components/cfg/AppCfg";

/**
 * Creates and configures all application services.
 *
 * This factory function is called once at application startup to instantiate
 * all services that will be provided through the ServicesContext.
 *
 * @param baseUrl - Optional API base override. Resolution order: baseUrl -> VITE_API_URL ->
 *                  legacy VITE_API_BASE_URL -> '/api/v1'.
 * @param getToken - Optional function to retrieve current auth token. If not provided,
 *                   token injection via AuthInterceptor global fetch override will be used instead.
 * @returns Services object with all configured services
 *
 * @example
 * ```tsx
 * // Usage at application startup
 * const services = createServices();
 * root.render(
 *   <ServicesProvider services={services}>
 *     <App />
 *   </ServicesProvider>
 * );
 * ```
 */
export function createServices(
  baseUrl?: string,
  getToken?: () => string | null,
): Services {
  const logger = new SentryLoggerService();

  const apiBaseUrl = resolveApiBaseUrlForLegacyCompat(baseUrl);
  const api = new HttpAPIService(apiBaseUrl, logger, getToken);

  const queryBuilderFactory = createQueryBuilderFactory();

  return {
    api,
    logger,
    queryBuilderFactory,
    // Phase 2: storeGroups module
    storeGroups: new StoreGroupService(api, queryBuilderFactory),
    // Domain services will be added here as modules migrate
    // Example:
    // user: new UserService(api, queryBuilderFactory),
    // projects: new ProjectService(api, queryBuilderFactory),
  };
}

// TODO(migration): Remove after all modules stop requiring legacy API URL compatibility.
function resolveApiBaseUrlForLegacyCompat(baseUrl?: string): string {
  const resolvedBaseUrl =
    baseUrl ||
    appConfiguration.apiUrl ||
    import.meta.env.VITE_API_BASE_URL ||
    "/api/v1";

  if (
    resolvedBaseUrl === appConfiguration.apiUrl &&
    appConfiguration.apiVersion
  ) {
    return `${appConfiguration.apiUrl}/${appConfiguration.apiVersion}`;
  }

  return resolvedBaseUrl;
}
